import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { apiUrl } from '../utils/apiBase';
import SubscriptionUsageCounter from '../components/SubscriptionUsageCounter';
import TermsModal from '../components/TermsModal';

const SubscriptionPage = () => {
  const { user, isAuthenticated, refreshUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingSubscription, setCreatingSubscription] = useState(false);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [isAnnual, setIsAnnual] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileSubmitting, setProfileSubmitting] = useState(false);
  const [pendingSubscribePlanId, setPendingSubscribePlanId] = useState(null);
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsContent, setTermsContent] = useState('');
  const [termsLoading, setTermsLoading] = useState(false);
  const [invoiceDownloading, setInvoiceDownloading] = useState(false);
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    address1: '',
    address2: '',
    zip: '',
    city: '',
    state: '',
    country: '',
    isRealEstateBusiness: '',
    companyName: '',
    taxNumber: '',
    acceptTerms: false,
  });

  const fetchPlans = useCallback(async () => {
    try {
      const response = await fetch(apiUrl('/subscriptions/plans'));
      const data = await response.json();

      if (data.success) {
        setPlans(data.data);
      } else {
        toast.error(t('subscriptionPage.failedToLoadPlans'));
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error(t('subscriptionPage.failedToLoadPlans'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchActiveSubscription = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setActiveSubscription(null);
        return;
      }

      const response = await fetch(apiUrl('/subscriptions/active'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status !== 404) {
          console.warn('Active subscription request failed with status', response.status);
        }
        setActiveSubscription(null);
        return;
      }

      const data = await response.json();
      if (data.success) {
        setActiveSubscription(data.data);
      } else {
        setActiveSubscription(null);
      }
    } catch (error) {
      console.error('Error fetching active subscription:', error);
      setActiveSubscription(null);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Only land seekers should see subscription page
    if (user?.role !== 'land_seeker') {
      navigate('/report'); // Redirect land reporters to report page
      return;
    }

    fetchPlans();
    fetchActiveSubscription();
    // Omit fetchPlans/fetchActiveSubscription from deps to avoid "Cannot access before initialization" in minified build
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.role, navigate]);

  const needsSubscriptionProfile = () => {
    if (!user || user?.role !== 'land_seeker') return false;
    const missingName = !String(user?.name || '').trim();
    const missingAddress = !String(user?.address || '').trim();
    const termsAccepted = !!user?.terms_accepted_at;
    const businessRequired = !!user?.is_real_estate_business && (!String(user?.company_name || '').trim() || !String(user?.tax_number || '').trim());
    return missingName || missingAddress || !termsAccepted || businessRequired;
  };

  const openProfileModalForSubscribe = (planId) => {
    setPendingSubscribePlanId(planId);
    const existingName = String(user?.name || '').trim();
    setProfileForm({
      firstName: existingName,
      middleName: '',
      lastName: '',
      address1: String(user?.address || '').trim(),
      address2: '',
      zip: '',
      city: '',
      state: '',
      country: '',
      isRealEstateBusiness: user?.is_real_estate_business ? 'yes' : '',
      companyName: user?.company_name || '',
      taxNumber: user?.tax_number || '',
      acceptTerms: false,
    });
    setShowProfileModal(true);
  };

  const beginSubscribe = async (planId) => {
    setCreatingSubscription(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(apiUrl('/subscriptions/create'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ plan_id: planId }),
      });

      const data = await response.json();
      
      if (data.success && data.data.approval_url) {
        // Redirect to PayPal for approval
        window.location.href = data.data.approval_url;
      } else {
        toast.error(data.message || t('subscriptionPage.failedToCreateSubscription'));
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast.error(t('subscriptionPage.failedToCreateSubscription'));
    } finally {
      setCreatingSubscription(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (needsSubscriptionProfile()) {
      openProfileModalForSubscribe(planId);
      return;
    }
    return beginSubscribe(planId);
  };

  const handleDownloadInvoice = async () => {
    try {
      setInvoiceDownloading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(apiUrl('/subscriptions/invoice'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf',
        },
      });

      if (!response.ok) {
        let message = t('subscriptionPage.invoice.unavailable');
        try {
          const data = await response.json();
          if (data?.message) message = data.message;
        } catch (err) {
          // ignore parse
        }
        toast.error(message);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const disposition = response.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      link.href = url;
      link.download = match?.[1] || 'invoice.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Invoice download failed:', error);
      toast.error(t('subscriptionPage.invoice.unavailable'));
    } finally {
      setInvoiceDownloading(false);
    }
  };

  const submitSubscriptionProfile = async () => {
    if (!profileForm.acceptTerms) {
      toast.error(t('subscriptionPage.profile.acceptTermsRequired'));
      return;
    }
    if (!String(profileForm.firstName || '').trim() || !String(profileForm.lastName || '').trim()) {
      toast.error(t('subscriptionPage.profile.nameRequired'));
      return;
    }
    if (!String(profileForm.address1 || '').trim() || !String(profileForm.zip || '').trim() || !String(profileForm.city || '').trim() || !String(profileForm.state || '').trim() || !String(profileForm.country || '').trim()) {
      toast.error(t('subscriptionPage.profile.addressRequired'));
      return;
    }
    if (profileForm.isRealEstateBusiness === 'yes') {
      if (!String(profileForm.companyName || '').trim() || !String(profileForm.taxNumber || '').trim()) {
        toast.error(t('subscriptionPage.profile.businessRequired'));
        return;
      }
    }

    const name = `${profileForm.firstName} ${profileForm.middleName ? profileForm.middleName + ' ' : ''}${profileForm.lastName}`.trim();
    const address = `${profileForm.address1}${profileForm.address2 ? ', ' + profileForm.address2 : ''}${profileForm.city ? ', ' + profileForm.city : ''}${profileForm.state ? ', ' + profileForm.state : ''}${profileForm.zip ? ' ' + profileForm.zip : ''}${profileForm.country ? ', ' + profileForm.country : ''}`.trim();

    setProfileSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(apiUrl('/auth/complete-subscription-profile'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          name,
          address,
          accept_terms: profileForm.acceptTerms,
          is_real_estate_business: profileForm.isRealEstateBusiness === 'yes',
          company_name: profileForm.isRealEstateBusiness === 'yes' ? profileForm.companyName : null,
          tax_number: profileForm.isRealEstateBusiness === 'yes' ? profileForm.taxNumber : null,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        toast.error(data?.message || t('subscriptionPage.profile.failedToSave'));
        return;
      }

      try {
        await refreshUser?.();
      } catch (e) {
        // Best-effort; ignore
      }

      setShowProfileModal(false);
      const planId = pendingSubscribePlanId;
      setPendingSubscribePlanId(null);

      if (planId) {
        await beginSubscribe(planId);
      }
    } catch (error) {
      console.error('Error saving subscription profile:', error);
      toast.error(t('subscriptionPage.profile.failedToSave'));
    } finally {
      setProfileSubmitting(false);
    }
  };

  const openTermsModal = async () => {
    setTermsOpen(true);
    if (termsContent) return;

    setTermsLoading(true);
    try {
      const response = await fetch(apiUrl('/terms?role=land_seeker'));
      const data = await response.json();
      if (response.ok && data.success) {
        setTermsContent(String(data.data?.content || ''));
      }
    } catch (error) {
      console.error('Failed to load terms of service:', error);
    } finally {
      setTermsLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    if (!activeSubscription) {
      toast.error(t('subscriptionPage.noActiveSubscriptionToUpgrade'));
      return;
    }

    if (!window.confirm(t('subscriptionPage.confirmUpgrade'))) {
      return;
    }

    setCreatingSubscription(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(apiUrl('/subscriptions/upgrade'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ 
          current_subscription_id: activeSubscription.id,
          new_plan_id: planId 
        }),
      });

      const data = await response.json();
      
      if (data.success && data.data.approval_url) {
        // Redirect to PayPal for approval
        window.location.href = data.data.approval_url;
      } else {
        toast.error(data.message || t('subscriptionPage.failedToUpgrade'));
      }
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      toast.error(t('subscriptionPage.failedToUpgrade'));
    } finally {
      setCreatingSubscription(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!activeSubscription) return;

    if (!window.confirm(t('subscriptionPage.confirmCancel'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(apiUrl('/subscriptions/cancel'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ subscription_id: activeSubscription.id }),
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(t('subscriptionPage.cancelSuccess'));
        // The backend keeps the subscription "entitled" until expires_at,
        // so refresh the active subscription instead of clearing it.
        if (data.data?.subscription) {
          setActiveSubscription(data.data.subscription);
        } else {
          fetchActiveSubscription();
        }
      } else {
        toast.error(data.message || t('subscriptionPage.failedToCancel'));
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error(t('subscriptionPage.failedToCancel'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('subscriptionPage.loadingPlans')}</p>
        </div>
      </div>
    );
  }

  if (!plans || plans.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('subscriptionPage.noPlans.title')}</h1>
            <p className="text-gray-600">{t('subscriptionPage.noPlans.body')}</p>
            <div className="mt-4 text-sm text-gray-500">{t('subscriptionPage.noPlans.tip')}</div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => window.location.reload()} className="btn-primary px-4 py-2">
                {t('common.retry')}
              </button>
              <button onClick={() => navigate('/')} className="btn-outline px-4 py-2">
                {t('subscriptionPage.backToHome')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Go Back Button */}
        <div className="mb-6">
          <button
            onClick={() => {
              // If the user arrived here after a PayPal redirect, browser history may point back to PayPal.
              // In that case, always go back to home instead.
              const ref = String(document.referrer || '').toLowerCase();
              const cameFromPaypal =
                ref.includes('paypal.com') ||
                ref.includes('sandbox.paypal.com') ||
                ref.includes('www.paypal.com');

              if (cameFromPaypal) {
                navigate('/');
              } else {
                navigate(-1);
              }
            }}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('common.back')}
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {activeSubscription ? t('subscriptionPage.header.manageTitle') : t('subscriptionPage.header.chooseTitle')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {activeSubscription 
              ? t('subscriptionPage.header.manageBody')
              : t('subscriptionPage.header.chooseBody')
            }
          </p>
          
          {/* Billing Toggle - Only show if no active subscription */}
          {!activeSubscription && (
            <div className="flex items-center justify-center space-x-4 mb-8">
              <span className={`text-lg font-medium ${!isAnnual ? 'text-gray-900' : 'text-gray-500'}`}>
                {t('subscriptionPage.billing.monthly')}
              </span>
              <button
                onClick={() => setIsAnnual(!isAnnual)}
                className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${
                  isAnnual ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                    isAnnual ? 'translate-x-9' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-lg font-medium ${isAnnual ? 'text-gray-900' : 'text-gray-500'}`}>
                {t('subscriptionPage.billing.annual')}
              </span>
              {isAnnual && (
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                  {t('subscriptionPage.billing.savePercent', { percent: 20 })}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Active Subscription Alert */}
        {activeSubscription && (
          <div className={`mb-8 border rounded-lg p-6 ${
            (activeSubscription.status === 'cancelled' || activeSubscription.cancel_at_period_end)
              ? 'bg-yellow-50 border-yellow-200'
              : 'bg-green-50 border-green-200'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className={`text-lg font-semibold ${
                  (activeSubscription.status === 'cancelled' || activeSubscription.cancel_at_period_end)
                    ? 'text-yellow-800'
                    : 'text-green-800'
                }`}>
                  {(activeSubscription.status === 'cancelled' || activeSubscription.cancel_at_period_end)
                    ? t('subscriptionPage.active.cancelledTitle')
                    : t('subscriptionPage.active.activeTitle')}: {activeSubscription.subscription_plan?.name}
                </h3>
                <p className={(activeSubscription.status === 'cancelled' || activeSubscription.cancel_at_period_end) ? 'text-yellow-700' : 'text-green-700'}>
                  ${activeSubscription.amount}/{activeSubscription.type} • Up to {activeSubscription.max_properties} properties •
                  {' '}
                  Usable until: {activeSubscription.expires_at ? new Date(activeSubscription.expires_at).toLocaleDateString() : '—'}
                  {activeSubscription.expires_at && (
                    <>
                      {' '}
                      (
                      {Math.max(
                        0,
                        Math.ceil((new Date(activeSubscription.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                      )}{' '}
                      days left)
                    </>
                  )}
                </p>
              </div>
              {(activeSubscription.status === 'cancelled' || activeSubscription.cancel_at_period_end) ? (
                <span className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg text-sm font-medium">
                  {t('subscriptionPage.active.autoRenewalStopped')}
                </span>
              ) : (
                <button
                  onClick={handleCancelSubscription}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  {t('subscriptionPage.active.cancelButton')}
                </button>
              )}
            </div>
          </div>
        )}

        {activeSubscription && (
          <div className="mb-8 border rounded-lg p-6 bg-white">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('subscriptionPage.invoice.title')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('subscriptionPage.invoice.body')}
                </p>
              </div>
              <button
                onClick={handleDownloadInvoice}
                disabled={invoiceDownloading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
              >
                {invoiceDownloading
                  ? t('subscriptionPage.invoice.downloading')
                  : t('subscriptionPage.invoice.download')}
              </button>
            </div>
          </div>
        )}

        {/* Subscription Usage (moved from /preferences) */}
        {activeSubscription && (
          <div className="mb-10 max-w-3xl mx-auto">
            <SubscriptionUsageCounter />
          </div>
        )}

        {/* Plans Grid - Show different content based on subscription status */}
        {!activeSubscription ? (
          // Show pricing table for new subscribers
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {plans
              .filter(plan => isAnnual ? plan.billing_cycle === 'yearly' : plan.billing_cycle === 'monthly')
              .map((plan) => (
              <div
                key={plan.id}
                className={`bg-white rounded-2xl shadow-lg p-8 relative ${
                  plan.name.includes('Pro') ? 'ring-2 ring-blue-500 scale-105' : ''
                }`}
              >
                {plan.name.includes('Pro') && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                      {t('subscriptionPage.plan.mostPopular')}
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-6">{plan.description}</p>
                  
                  <div className="mb-6">
                    <span className="text-5xl font-bold text-gray-900">${plan.price}</span>
                    <span className="text-gray-600 ml-2">
                      /{isAnnual ? t('subscriptionPage.billing.year') : t('subscriptionPage.billing.month')}
                    </span>
                  </div>

                  {isAnnual && (
                    <div className="mb-4">
                      <div className="text-gray-500 text-sm mb-2">
                        {t('subscriptionPage.plan.billedAnnuallyPerMonth', { price: Math.round(plan.price / 12) })}
                      </div>
                      <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                        {t('subscriptionPage.plan.saveWithYearly', { percent: 20 })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4 mb-8">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">{t('subscriptionPage.plan.searchProperties', { count: plan.max_properties })}</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">{t('subscriptionPage.plan.contactSellers')}</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">{t('subscriptionPage.plan.advancedFilters')}</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">{t('subscriptionPage.plan.cancelAnytime')}</span>
                  </div>
                </div>

                <button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={creatingSubscription}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                    plan.name.includes('Pro')
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  } ${
                    creatingSubscription ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {creatingSubscription ? t('subscriptionPage.plan.creating') : t('subscriptionPage.plan.subscribeNow')}
                </button>
              </div>
            ))}
          </div>
        ) : (
          // Show upgrade options for existing subscribers
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {plans
              .filter(plan => plan.billing_cycle === activeSubscription.type)
              .map((plan) => {
                const isCurrentPlan = plan.id === activeSubscription.subscription_plan?.id;
                const isUpgrade = plan.max_properties > activeSubscription.max_properties;
                
                return (
                <div
                  key={plan.id}
                  className={`bg-white rounded-2xl shadow-lg p-8 relative ${
                    isCurrentPlan ? 'ring-2 ring-green-500 bg-green-50' : 
                    isUpgrade ? 'ring-2 ring-blue-500 scale-105' : 'opacity-75'
                  }`}
                >
                  {isCurrentPlan && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                        {t('subscriptionPage.plan.currentPlan')}
                      </span>
                    </div>
                  )}
                  
                  {isUpgrade && !isCurrentPlan && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                        {t('subscriptionPage.plan.upgradeAvailable')}
                      </span>
                    </div>
                  )}

                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-600 mb-6">{plan.description}</p>
                    
                    <div className="mb-6">
                      <span className="text-5xl font-bold text-gray-900">${plan.price}</span>
                      <span className="text-gray-600 ml-2">
                        /{plan.billing_cycle === 'monthly' ? t('subscriptionPage.billing.month') : plan.billing_cycle === 'yearly' ? t('subscriptionPage.billing.year') : plan.billing_cycle}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4 mb-8">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">{t('subscriptionPage.plan.searchProperties', { count: plan.max_properties })}</span>
                    </div>
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">{t('subscriptionPage.plan.contactSellers')}</span>
                    </div>
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">{t('subscriptionPage.plan.advancedFilters')}</span>
                    </div>
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">{t('subscriptionPage.plan.cancelAnytime')}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => isUpgrade ? handleUpgrade(plan.id) : handleSubscribe(plan.id)}
                    disabled={creatingSubscription || isCurrentPlan}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                      isCurrentPlan
                        ? 'bg-green-600 text-white cursor-not-allowed'
                        : isUpgrade
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    } ${
                      creatingSubscription ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {creatingSubscription ? t('subscriptionPage.plan.processing') : 
                     isCurrentPlan ? t('subscriptionPage.plan.currentPlan') : 
                     isUpgrade ? t('subscriptionPage.plan.upgradeNow') : t('subscriptionPage.plan.subscribe')}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Features Section */}
        <div className="mt-16 bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            {t('subscriptionPage.why.title')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('subscriptionPage.why.advancedSearchTitle')}</h3>
              <p className="text-gray-600">{t('subscriptionPage.why.advancedSearchBody')}</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('subscriptionPage.why.directContactTitle')}</h3>
              <p className="text-gray-600">{t('subscriptionPage.why.directContactBody')}</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('subscriptionPage.why.verifiedTitle')}</h3>
              <p className="text-gray-600">{t('subscriptionPage.why.verifiedBody')}</p>
            </div>
          </div>
        </div>

        {/* Profile modal (required before subscribing) */}
        {showProfileModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div className="w-full max-w-lg bg-white rounded-xl shadow-xl p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">{t('subscriptionPage.profileModal.title')}</h2>
              <p className="text-gray-600 mb-6">
                {t('subscriptionPage.profileModal.subtitle')}
              </p>

              <div className="space-y-4">
                {/* Name fields (same layout as land reporter onboarding) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscriptionPage.profileModal.firstName')}</label>
                    <input
                      value={profileForm.firstName}
                      onChange={(e) => setProfileForm((p) => ({ ...p, firstName: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-md"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscriptionPage.profileModal.middleName')}</label>
                    <input
                      value={profileForm.middleName}
                      onChange={(e) => setProfileForm((p) => ({ ...p, middleName: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscriptionPage.profileModal.lastName')}</label>
                    <input
                      value={profileForm.lastName}
                      onChange={(e) => setProfileForm((p) => ({ ...p, lastName: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-md"
                      required
                    />
                  </div>
                </div>

                {/* Address fields (same layout as land reporter onboarding) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscriptionPage.profileModal.address1')}</label>
                  <input
                    value={profileForm.address1}
                    onChange={(e) => setProfileForm((p) => ({ ...p, address1: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscriptionPage.profileModal.address2')}</label>
                  <input
                    value={profileForm.address2}
                    onChange={(e) => setProfileForm((p) => ({ ...p, address2: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscriptionPage.profileModal.zip')}</label>
                    <input
                      value={profileForm.zip}
                      onChange={(e) => setProfileForm((p) => ({ ...p, zip: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-md"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscriptionPage.profileModal.city')}</label>
                    <input
                      value={profileForm.city}
                      onChange={(e) => setProfileForm((p) => ({ ...p, city: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-md"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscriptionPage.profileModal.state')}</label>
                    <input
                      value={profileForm.state}
                      onChange={(e) => setProfileForm((p) => ({ ...p, state: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-md"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscriptionPage.profileModal.country')}</label>
                    <input
                      value={profileForm.country}
                      onChange={(e) => setProfileForm((p) => ({ ...p, country: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-md"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscriptionPage.profileModal.isRealEstateBusiness')}</label>
                    <select
                      value={profileForm.isRealEstateBusiness}
                      onChange={(e) => setProfileForm((p) => ({
                        ...p,
                        isRealEstateBusiness: e.target.value,
                        companyName: e.target.value === 'yes' ? p.companyName : '',
                        taxNumber: e.target.value === 'yes' ? p.taxNumber : '',
                      }))}
                      className="w-full px-3 py-2 border rounded-md"
                      required
                    >
                      <option value="">{t('common.select')}</option>
                      <option value="yes">{t('common.yes')}</option>
                      <option value="no">{t('common.no')}</option>
                    </select>
                  </div>
                  <div />
                </div>

                {profileForm.isRealEstateBusiness === 'yes' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscriptionPage.profileModal.companyName')}</label>
                      <input
                        value={profileForm.companyName}
                        onChange={(e) => setProfileForm((p) => ({ ...p, companyName: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-md"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscriptionPage.profileModal.taxNumber')}</label>
                      <input
                        value={profileForm.taxNumber}
                        onChange={(e) => setProfileForm((p) => ({ ...p, taxNumber: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-md"
                        required
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-2">
                  <input
                    id="acceptTermsSub"
                    type="checkbox"
                    checked={profileForm.acceptTerms}
                    onChange={(e) => setProfileForm((p) => ({ ...p, acceptTerms: e.target.checked }))}
                    className="mt-1 h-4 w-4"
                  />
                  <label htmlFor="acceptTermsSub" className="text-sm text-gray-700">
                    {t('subscriptionPage.profileModal.acceptTermsPrefix')}{' '}
                    <button
                      type="button"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        openTermsModal();
                      }}
                      className="text-blue-600 underline hover:text-blue-800"
                    >
                      {t('common.termsOfService')}
                    </button>
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700"
                    onClick={() => {
                      setShowProfileModal(false);
                      setPendingSubscribePlanId(null);
                    }}
                    disabled={profileSubmitting}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={submitSubscriptionProfile}
                    disabled={profileSubmitting}
                  >
                    {profileSubmitting ? t('subscriptionPage.profileModal.saving') : t('subscriptionPage.profileModal.continueToPaypal')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <TermsModal
        isOpen={termsOpen}
        onClose={() => setTermsOpen(false)}
        title={t('common.termsOfService')}
        content={termsContent}
        loading={termsLoading}
      />
    </div>
  );
};

export default SubscriptionPage;
